import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { FileText, Plus, Search, Filter, X, FolderOpen, Sparkles, ChevronDown } from 'lucide-react'
import { documentsApi, FileUploadResult } from '../services/api'
import FileUpload from '../components/shared/FileUpload'
import { useProject } from '../contexts/ProjectContext'

const documentTypes = [
  { value: 'OpiniaPrawna', label: 'Opinia' },
  { value: 'Pozew', label: 'Pozew' },
  { value: 'Umowa', label: 'Umowa' },
  { value: 'PismoProcesowe', label: 'Pismo procesowe' },
  { value: 'Wezwanie', label: 'Wezwanie' },
  { value: 'Odwolanie', label: 'Odwołanie' },
  { value: 'Skarga', label: 'Skarga' },
  { value: 'Wniosek', label: 'Wniosek' },
  { value: 'Notatka', label: 'Notatka' },
  { value: 'OdpowiedzNaPozew', label: 'Odpowiedź na pozew' },
  { value: 'DodatkoweInformacje', label: 'Dodatkowe Informacje' },
]

const docTypeLabel = (type: string) =>
  documentTypes.find(d => d.value === type)?.label ?? type

export default function DocumentsPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filterType, setFilterType] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newDocTitle, setNewDocTitle] = useState('')
  const [newDocType, setNewDocType] = useState('OpiniaPrawna')
  const [uploadedContent, setUploadedContent] = useState('')
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null)
  const [useOcr, setUseOcr] = useState(true)

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentProject } = useProject()

  // Debounce search input (300ms)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [search])

  const { data, isLoading } = useQuery({
    queryKey: ['documents', { page, projectId: currentProject?.id, search: debouncedSearch, type: filterType }],
    queryFn: () => documentsApi.list({
      page,
      projectId: currentProject?.id,
      search: debouncedSearch || undefined,
      type: filterType || undefined,
    }).then(r => r.data),
    enabled: !!currentProject, // Only fetch when a project is selected
  })

  const createMutation = useMutation({
    mutationFn: (data: { title: string; type: string; content?: string; projectId: string; fileId?: string }) =>
      documentsApi.create(data).then(r => r.data),
    onSuccess: (newDoc) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      handleCloseModal()
      // Navigate to the new document
      if (newDoc?.id) {
        navigate(`/documents/${newDoc.id}`)
      }
    },
  })

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setNewDocTitle('')
    setNewDocType('OpiniaPrawna')
    setUploadedContent('')
    setUploadedFileId(null)
  }

  const handleUploadComplete = (results: FileUploadResult[]) => {
    // Get first successful result
    const successResult = results.find(r => r.status === 'success')
    if (successResult) {
      setUploadedContent(successResult.text)
      if (successResult.fileId) {
        setUploadedFileId(successResult.fileId)
      }
      // Auto-fill title from filename (remove extension)
      if (!newDocTitle) {
        const titleFromFile = successResult.filename.replace(/\.[^/.]+$/, '')
        setNewDocTitle(titleFromFile)
      }
    } else {
      console.warn('[DocumentsPage] No successful upload result found')
    }
  }

  const handleCreateDocument = (e: React.FormEvent) => {
    e.preventDefault()
    if (newDocTitle.trim() && currentProject) {
      const payload = {
        title: newDocTitle,
        type: newDocType,
        content: uploadedContent || undefined,
        projectId: currentProject.id,
        ...(uploadedFileId && { fileId: uploadedFileId }),
      }
      createMutation.mutate(payload)
    }
  }

  // Show message when no project is selected
  if (!currentProject) {
    return (
      <div className="space-y-6">
        <div className="card p-8 text-center">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Wybierz projekt
          </h2>
          <p className="text-gray-500 mb-4">
            Aby zobaczyć dokumenty, wybierz projekt z menu po lewej stronie.
          </p>
          <Link to="/projects" className="btn-primary inline-flex items-center">
            <FolderOpen className="w-4 h-4 mr-2" />
            Przejdź do projektów
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <FolderOpen className="w-4 h-4" />
            <span>{currentProject.name}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Dokumenty</h1>
          <p className="text-gray-500">Zarządzaj dokumentami w projekcie</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Nowy dokument
        </button>
      </div>

      {/* New Document Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-gray-600 bg-opacity-50"
            onClick={handleCloseModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Nowy dokument</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateDocument} className="space-y-4">
              {/* File upload section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prześlij plik (opcjonalnie)
                </label>
                <FileUpload
                  onUploadComplete={handleUploadComplete}
                  useOcr={useOcr}
                  onUseOcrChange={setUseOcr}
                />
              </div>

              {/* Uploaded content preview */}
              {uploadedContent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Podgląd treści ({uploadedContent.length} znaków)
                  </label>
                  <div className="p-3 bg-gray-50 rounded border text-sm text-gray-600 max-h-32 overflow-y-auto">
                    {uploadedContent.slice(0, 500)}
                    {uploadedContent.length > 500 && '...'}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tytuł dokumentu *
                </label>
                <input
                  type="text"
                  value={newDocTitle}
                  onChange={(e) => setNewDocTitle(e.target.value)}
                  placeholder="Wprowadź tytuł..."
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ dokumentu *
                </label>
                <select
                  value={newDocType}
                  onChange={(e) => setNewDocType(e.target.value)}
                  className="input w-full"
                >
                  {documentTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !newDocTitle.trim() || !uploadedContent}
                  className="btn-primary"
                >
                  {createMutation.isPending ? 'Tworzenie...' : 'Utwórz'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div className="space-y-3">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Szukaj dokumentów..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <button
            className={`btn-secondary ${showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}`}
            onClick={() => setShowFilters(v => !v)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtry
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Typ dokumentu:</label>
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(1) }}
              className="input text-sm py-1.5"
            >
              <option value="">Wszystkie typy</option>
              {documentTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {filterType && (
              <button
                className="text-sm text-gray-500 hover:text-gray-700 underline"
                onClick={() => { setFilterType(''); setPage(1) }}
              >
                Wyczyść
              </button>
            )}
          </div>
        )}
      </div>

      {/* Documents list */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          </div>
        ) : data?.items?.length ? (
          <>
            {(() => {
              const generated = data.items.filter(d => d.isGenerated)
              const attached = data.items.filter(d => !d.isGenerated)
              return (
                <>
                  {generated.length > 0 && (
                    <>
                      <div className="px-4 pt-4 pb-2">
                        <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5" />
                          Wygenerowane dokumenty (AI)
                        </h3>
                      </div>
                      <div className="divide-y">
                        {generated.map((doc) => (
                          <Link
                            key={doc.id}
                            to={`/documents/${doc.id}`}
                            className="flex items-center p-4 hover:bg-gray-50"
                          >
                            <Sparkles className="w-8 h-8 text-amber-500 mr-4 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900">{doc.title}</p>
                              <p className="text-sm text-gray-500">
                                Utworzono: {new Date(doc.createdAt).toLocaleDateString('pl-PL')} •
                                Wersja {doc.currentVersion}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">AI</span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {docTypeLabel(doc.type)}
                              </span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </>
                  )}

                  {attached.length > 0 && (
                    <>
                      <div className={`px-4 pt-4 pb-2 ${generated.length > 0 ? 'border-t' : ''}`}>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5" />
                          Załączone dokumenty
                        </h3>
                      </div>
                      <div className="divide-y">
                        {attached.map((doc) => (
                          <Link
                            key={doc.id}
                            to={`/documents/${doc.id}`}
                            className="flex items-center p-4 hover:bg-gray-50"
                          >
                            <FileText className="w-8 h-8 text-gray-400 mr-4 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900">{doc.title}</p>
                              <p className="text-sm text-gray-500">
                                Utworzono: {new Date(doc.createdAt).toLocaleDateString('pl-PL')} •
                                Wersja {doc.currentVersion}
                              </p>
                            </div>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded flex-shrink-0">
                              {docTypeLabel(doc.type)}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )
            })()}

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-gray-500">
                  Strona {page} z {data.totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-secondary"
                  >
                    Poprzednia
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                    disabled={page === data.totalPages}
                    className="btn-secondary"
                  >
                    Następna
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Brak dokumentów</p>
          </div>
        )}
      </div>
    </div>
  )
}
